<script setup>
import { ref, computed, nextTick } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';

import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import FormInput from 'v3/components/Form/Input.vue';
import NativeSelect from 'dashboard/components-next/native-select/NativeSelect.vue';
import { getHelpUrlForFeature } from 'dashboard/helper/featureHelper';

const emit = defineEmits(['close']);

const dialogRef = ref(null);
const store = useStore();
const { t } = useI18n();

const agentEmail = ref('');
const selectedRoleId = ref('agent');
const restrictContactVisibility = ref(false);
const contactVisibilityScope = ref('user');
const advancedOpen = ref(true);

const rules = {
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const helpURL = computed(() => getHelpUrlForFeature('agents'));

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'owner',
      name: 'owner',
      label: t('AGENT_MGMT.AGENT_TYPES.OWNER'),
    },
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRolesList = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRolesList];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const isAgentRole = computed(() => {
  const roleName = selectedRole.value?.name || '';
  return roleName === 'agent' || roleName.startsWith('custom_');
});

const roleDescription = computed(() => {
  const role = selectedRole.value;
  if (!role) return '';
  if (role.name === 'owner') {
    return t('AGENT_MGMT.ADD_MODAL.ROLE_DESC.OWNER');
  }
  if (role.name === 'administrator') {
    return t('AGENT_MGMT.ADD_MODAL.ROLE_DESC.ADMINISTRATOR');
  }
  if (role.name === 'agent') {
    return t('AGENT_MGMT.ADD_MODAL.ROLE_DESC.AGENT');
  }
  return t('AGENT_MGMT.ADD_MODAL.ROLE_DESC.CUSTOM');
});

function inviteNameFromEmail(addr) {
  const trimmed = addr?.trim();
  if (!trimmed) return 'Agent';
  const local = trimmed.split('@')[0]?.trim();
  return local || 'Agent';
}

function resetForm() {
  agentEmail.value = '';
  selectedRoleId.value = 'agent';
  restrictContactVisibility.value = false;
  contactVisibilityScope.value = 'user';
  advancedOpen.value = true;
}

function close() {
  dialogRef.value?.close();
}

function handleDialogClose() {
  resetForm();
  v$.value.$reset();
  emit('close');
}

function open() {
  resetForm();
  nextTick(() => v$.value.$reset());
  dialogRef.value?.open();
}

defineExpose({ open, close });

function suppressEnterSubmit(e) {
  if (e.target?.tagName === 'TEXTAREA') return;
  e.preventDefault();
}

async function addAgent() {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (!selectedRole.value) return;

  try {
    const payload = {
      name: inviteNameFromEmail(agentEmail.value),
      email: agentEmail.value.trim(),
      restrictions: {
        restrict_contact_visibility:
          isAgentRole.value && restrictContactVisibility.value,
        contact_visibility_scope: contactVisibilityScope.value,
      },
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    close();
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error || {};

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('AGENT_MGMT.ADD_MODAL.TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    width="3xl"
    @close="handleDialogClose"
  >
    <template #description>
      <p
        class="mb-0 border-b border-n-weak pb-4 text-sm leading-relaxed text-n-slate-11"
      >
        {{ t('AGENT_MGMT.ADD_MODAL.INTRO') }}
      </p>
    </template>

    <form
      class="grid w-full gap-5"
      @submit.prevent="addAgent"
      @keydown.enter.exact="suppressEnterSubmit"
    >
      <div class="grid gap-5 md:grid-cols-2">
        <FormInput
          v-model="agentEmail"
          name="add-agent-email"
          type="email"
          spacing="compact"
          :label="t('AGENT_MGMT.ADD_MODAL.EMAIL_LABEL')"
          :placeholder="t('AGENT_MGMT.ADD_MODAL.EMAIL_PLACEHOLDER')"
          :has-error="v$.agentEmail.$error"
          :error-message="
            v$.agentEmail.$error ? v$.agentEmail.$errors[0].$message : ''
          "
          @blur="v$.agentEmail.$touch"
        />

        <div class="flex min-w-0 flex-col">
          <label
            class="mb-0 flex justify-between text-sm font-medium leading-6 text-n-slate-12"
            :class="{ 'text-n-ruby-12': v$.selectedRoleId.$error }"
            for="add-agent-access-level"
          >
            {{ t('AGENT_MGMT.ADD_MODAL.ACCESS_LEVEL_LABEL') }}
          </label>
          <div class="mt-1">
            <NativeSelect
              id="add-agent-access-level"
              v-model="selectedRoleId"
              :has-error="v$.selectedRoleId.$error"
              @change="v$.selectedRoleId.$touch"
            >
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.label }}
              </option>
            </NativeSelect>
          </div>
          <p
            v-if="v$.selectedRoleId.$error"
            class="mt-1.5 text-sm text-n-ruby-9"
          >
            {{ t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
          </p>
        </div>
      </div>

      <p class="mb-0 text-sm leading-relaxed text-n-slate-11">
        {{ roleDescription }}
      </p>

      <div
        class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-alpha-1 px-3 py-3"
      >
        <button
          type="button"
          class="flex w-full items-center justify-between gap-2 text-left text-sm font-semibold text-n-blue-11"
          @click="advancedOpen = !advancedOpen"
        >
          {{ t('AGENT_MGMT.ADD_MODAL.ADVANCED_TITLE') }}
          <Icon
            :icon="
              advancedOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
            class="size-5 shrink-0 text-n-blue-11"
          />
        </button>

        <div
          v-show="advancedOpen"
          class="flex flex-col gap-4 border-t border-n-weak pt-4"
        >
          <template v-if="isAgentRole">
            <div
              class="flex flex-col gap-3 md:flex-row md:items-start md:gap-6"
            >
              <label
                class="flex min-w-0 flex-1 cursor-pointer items-start gap-3"
              >
                <input
                  v-model="restrictContactVisibility"
                  type="checkbox"
                  class="mt-1 size-4 shrink-0 rounded border-n-weak text-n-brand"
                />
                <span class="flex min-w-0 flex-col gap-1">
                  <span class="text-sm font-semibold text-n-slate-12">
                    {{ $t('AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.LABEL') }}
                  </span>
                  <span class="text-sm leading-relaxed text-n-slate-11">
                    {{
                      $t(
                        'AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.DESCRIPTION'
                      )
                    }}
                  </span>
                </span>
              </label>

              <div
                v-if="restrictContactVisibility"
                class="flex w-full min-w-0 flex-col gap-1 md:w-64 md:flex-shrink-0"
              >
                <label
                  class="mb-0 text-sm font-semibold leading-6 text-n-slate-12"
                  for="add-agent-contact-scope"
                >
                  {{
                    $t('AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.SCOPE_LABEL')
                  }}
                </label>
                <NativeSelect
                  id="add-agent-contact-scope"
                  v-model="contactVisibilityScope"
                >
                  <option value="team">
                    {{
                      $t(
                        'AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.SCOPE_TEAM'
                      )
                    }}
                  </option>
                  <option value="user">
                    {{
                      $t(
                        'AGENT_MGMT.RESTRICTIONS.CONTACT_VISIBILITY.SCOPE_USER'
                      )
                    }}
                  </option>
                </NativeSelect>
              </div>
            </div>
          </template>
          <p v-else class="mb-0 text-sm leading-relaxed text-n-slate-11">
            {{ t('AGENT_MGMT.ADD_MODAL.ADVANCED_AGENT_ONLY') }}
          </p>
        </div>
      </div>
    </form>

    <template #footer>
      <div
        class="flex w-full flex-wrap items-center justify-between gap-4 border-t border-n-weak pt-4"
      >
        <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
          <a
            v-if="helpURL"
            :href="helpURL"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-sm font-semibold text-n-blue-11 hover:underline"
          >
            {{ $t('AGENT_MGMT.ADD_MODAL.LEARN_MORE_LINK') }}
            <Icon
              icon="i-lucide-chevron-right"
              class="size-4 shrink-0 text-n-blue-11"
            />
          </a>
        </CustomBrandPolicyWrapper>
        <div v-if="!helpURL" />

        <div class="flex items-center gap-3">
          <Button
            variant="link"
            slate
            type="button"
            class="!font-bold"
            :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click="close"
          />
          <Button
            blue
            type="button"
            class="rounded-xl !font-bold"
            :label="$t('AGENT_MGMT.ADD_MODAL.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isCreating"
            :is-loading="uiFlags.isCreating"
            @click="addAgent"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
